import React, { useState, useEffect, useRef } from 'react';
import './promo.css';
import { PROMO_CONFIG } from '../../config/promotions';
import Loader from '../ui/loader';
import { selectPromo, recordImpression, recordClick } from '../../api/promos';
import { ActivePromo } from '../../types/promo';
import PromoOverlayText from '../Promos/PromoOverlayText';

interface PromoProps {
    isPopup?: boolean;
    pageName?: string;
}

type PromoPlacement = 'desktopSidebar' | 'interspersed' | 'popup';

const lastShownKeyFor = (placement: PromoPlacement): string => {
    if (placement === 'interspersed') return PROMO_CONFIG.SESSION_KEYS.LAST_INTERSPERSED_PROMO;
    if (placement === 'desktopSidebar') return PROMO_CONFIG.SESSION_KEYS.LAST_SIDEBAR_PROMO;
    return PROMO_CONFIG.SESSION_KEYS.LAST_POPUP_PROMO;
};

const PromoContent: React.FC<PromoProps> = ({ isPopup = false, pageName = 'discover' }) => {
    const [showOverlay, setShowOverlay] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [currentPromo, setCurrentPromo] = useState<ActivePromo | null>(null);
    const [serveToken, setServeToken] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [creativeLoaded, setCreativeLoaded] = useState(false);
    const impressionRecordedRef = useRef<string | null>(null);

    // The placement this instance is selecting from, used for analytics + selection logic
    const currentPlacement: PromoPlacement = isPopup ? 'popup' : (!isMobile ? 'desktopSidebar' : 'interspersed');

    useEffect(() => {
        // Check if mobile
        const checkIfMobile = () => {
            setIsMobile(window.innerWidth < PROMO_CONFIG.MOBILE_BREAKPOINT);
        };

        checkIfMobile();
        window.addEventListener('resize', checkIfMobile);

        // For mobile and overlay mode, check cooldown and show if appropriate
        if (isPopup && isMobile) {
            const lastShown = localStorage.getItem(PROMO_CONFIG.SESSION_KEYS.LAST_POPUP_SHOWN);
            const now = Date.now();

            // Show if never shown before or if cooldown period has passed
            if (!lastShown || (now - parseInt(lastShown)) > PROMO_CONFIG.POPUP_COOLDOWN) {
                const timer = setTimeout(() => {
                    setShowOverlay(true);
                    // Store the current timestamp when showing the overlay
                    localStorage.setItem(PROMO_CONFIG.SESSION_KEYS.LAST_POPUP_SHOWN, now.toString());
                }, PROMO_CONFIG.POPUP_DELAY);

                return () => {
                    clearTimeout(timer);
                    window.removeEventListener('resize', checkIfMobile);
                };
            }
        }

        return () => window.removeEventListener('resize', checkIfMobile);
    }, [isPopup, isMobile]);

    // Ask the backend to pick the promo for this placement. Selection
    // (eligibility, funding, weighting) is authoritative server-side; the
    // client just tells it which slot it's filling and which promo it last
    // showed here (for rotation), same as the old client-side algorithm did.
    useEffect(() => {
        let cancelled = false;
        setIsLoading(true);
        setCurrentPromo(null);
        setServeToken(null);
        setCreativeLoaded(false);

        const device = isMobile ? 'mobile' : 'desktop';
        const lastShownKey = lastShownKeyFor(currentPlacement);
        const excludeId = localStorage.getItem(lastShownKey) || undefined;
        selectPromo(pageName, currentPlacement, device, excludeId)
            .then((result) => {
                if (cancelled) return;
                setCurrentPromo(result.promo);
                setServeToken(result.serveToken);
                setCreativeLoaded(false);
                if (result.promo) {
                    localStorage.setItem(lastShownKey, result.promo.id);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setCurrentPromo(null);
                    setServeToken(null);
                    setCreativeLoaded(false);
                }
            });

        // Fail-safe: Set loading to false after a maximum timeout
        const loadingTimeout = setTimeout(() => {
            if (!cancelled) setIsLoading(false);
        }, 5000); // 5 seconds max loading time

        return () => {
            cancelled = true;
            clearTimeout(loadingTimeout);
        };
    }, [isPopup, isMobile, pageName, currentPlacement]);

    // Record an impression once the creative has loaded and is visible.
    // Broken images must not bill CPM. Keyed by (promo id, serve token) so a
    // fresh selection always fires a fresh impression, while re-renders of the
    // same selection don't double-count.
    useEffect(() => {
        if (!currentPromo || !serveToken || !creativeLoaded) return;
        if (isPopup && isMobile && !showOverlay) return; // popups only "shown" once the overlay renders

        const impressionKey = `${currentPromo.id}-${serveToken}`;
        if (impressionRecordedRef.current === impressionKey) return;
        impressionRecordedRef.current = impressionKey;

        recordImpression(currentPromo.id, serveToken).catch(() => {
            // Failures shouldn't disrupt the promo experience; the serve
            // token simply goes unredeemed (and expires) in that case.
        });
    }, [currentPromo, serveToken, creativeLoaded, isPopup, isMobile, showOverlay]);

    const handlePromoClick = () => {
        if (!currentPromo || !serveToken) return;
        recordClick(currentPromo.id, serveToken).catch(() => {
            // Analytics failures shouldn't block navigation
        });
    };

    const closeOverlay = () => {
        setShowOverlay(false);
    };

    const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
        console.error('Promotion image failed to load:', currentPromo?.imageUrl);
        setIsLoading(false);
        setCreativeLoaded(false);
    };

    const handleImageLoad = () => {
        setIsLoading(false);
        setCreativeLoaded(true);
    };

    // If no promotion is available, don't render anything (keep a loader
    // visible while a new page's selection is in flight).
    if (!currentPromo) {
        if (isLoading && !isPopup) {
            return (
                <div className="relative w-full aspect-square rounded-lg overflow-hidden">
                    <div className="absolute inset-0 flex items-center justify-center">
                        <Loader size="sm" />
                    </div>
                </div>
            );
        }
        return null;
    }

    // Regular content display - all promo media is served at a 1:1 ratio
    if (!isPopup || !isMobile) {
        return (
            <div className="relative w-full aspect-square rounded-lg overflow-hidden">
                {isLoading ? (
                    <div className="absolute inset-0 flex items-center justify-center">
                        <Loader size="sm" />
                    </div>
                ) : null}
                <a
                    href={currentPromo.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full h-full"
                    onClick={handlePromoClick}
                >
                    <div className="absolute top-0 left-0 right-0 z-10">
                        <div className="w-full bg-black bg-opacity-25 text-gray-300 text-[9px] leading-none text-center py-0.5">
                            Sponsored Content
                        </div>
                    </div>
                    <img
                        src={currentPromo.imageUrl}
                        alt={currentPromo.title}
                        className="w-full h-full object-cover"
                        style={{ objectPosition: `${currentPromo.imagePosition?.xPercent ?? 50}% ${currentPromo.imagePosition?.yPercent ?? 50}%` }}
                        loading="lazy"
                        onLoad={handleImageLoad}
                        onError={handleImageError}
                    />
                    <PromoOverlayText textOverlay={currentPromo.textOverlay} />
                </a>
                <div className="absolute bottom-0 left-0 right-0 z-10">
                    <div className="w-full bg-black bg-opacity-25 text-gray-300 text-[9px] leading-none text-center py-0.5">
                        Sponsored Content
                    </div>
                </div>
            </div>
        );
    }

    // Overlay content for mobile
    return (
        <>
            {showOverlay && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black bg-opacity-50" />
                    <div className="relative z-10 bg-white rounded-lg overflow-hidden shadow-lg w-full max-w-sm">
                        <button
                            onClick={closeOverlay}
                            className="absolute top-4 right-2 z-20 bg-black bg-opacity-50 text-white rounded-full w-8 h-8 flex items-center justify-center"
                            aria-label="Close content"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                        </button>
                        {isLoading ? (
                            <div className="absolute inset-0 flex items-center justify-center">
                                <Loader size="sm" />
                            </div>
                        ) : null}
                        <a
                            href={currentPromo.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block w-full"
                            onClick={handlePromoClick}
                        >
                            <div className="w-full bg-black bg-opacity-30 text-gray-300 text-[9px] leading-none text-center py-0.5">
                                Sponsored Content
                            </div>
                            <div className="relative w-full aspect-square overflow-hidden">
                                <img
                                    src={currentPromo.imageUrl}
                                    alt={currentPromo.title}
                                    className="absolute inset-0 w-full h-full object-cover"
                                    style={{ objectPosition: `${currentPromo.imagePosition?.xPercent ?? 50}% ${currentPromo.imagePosition?.yPercent ?? 50}%` }}
                                    loading="lazy"
                                    onLoad={handleImageLoad}
                                    onError={handleImageError}
                                />
                                <PromoOverlayText textOverlay={currentPromo.textOverlay} />
                            </div>
                        </a>
                        <div className="w-full bg-black bg-opacity-30 text-gray-300 text-[9px] leading-none text-center py-0.5">
                            Sponsored Content
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default PromoContent;
